import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';

const BlogListing = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchBlogs(page);
  }, [page]);
  
  // Handle scroll event for infinite loading
  const handleScroll = useCallback(() => {
    if (!hasMore || loading || loadingMore || initialLoad) return;
    
    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Load more when user is within 100px of bottom
    if (scrollTop + windowHeight >= documentHeight - 100) {
      setPage(prevPage => prevPage + 1);
    }
  }, [hasMore, loading, loadingMore, initialLoad]);
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const fetchBlogs = async (pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Fetch blogs with pagination (9 per page)
      const response = await api.get(`/blogs?page=${pageNum}&per_page=9`);
      
      let blogsData = [];
      let paginationData = null;
      
      if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          blogsData = response.data.data;
        } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
          blogsData = response.data.data.data;
          paginationData = response.data.data;
        }
      } else if (Array.isArray(response.data)) {
        blogsData = response.data;
      }
      
      // Check if there are more blogs to load
      if (paginationData && paginationData.current_page && paginationData.last_page) {
        setHasMore(paginationData.current_page < paginationData.last_page);
      } else if (blogsData.length < 9) {
        setHasMore(false);
      }
      
      if (pageNum === 1) {
        setBlogs(blogsData);
      } else {
        setBlogs(prevBlogs => [...prevBlogs, ...blogsData]);
      }
      
      setInitialLoad(false);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs');
      setHasMore(false);
    } finally {
      if (pageNum === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    const baseUrl = api.defaults.baseURL?.replace('/api', '') || 'http://127.0.0.1:8000';
    
    if (imagePath.startsWith('/storage/')) {
      return `${baseUrl}${imagePath}`;
    } else if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    } else {
      return `${baseUrl}/storage/${imagePath}`;
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <section className="py-5" style={{ backgroundColor: '#fff', marginTop: '0px' }}>
      <div className="container">
        {/* Title */}
        <div className="row mb-5">
          <div className="col-12 text-center">
            <h1 className="display-5 fw-bold" style={{ marginBottom: '30px', textAlign: 'center' }}>
              <span style={{ color: 'red' }}>सोच</span> 
              <span style={{ color: 'green', margin: '0 10px' }}>•</span> 
              <span style={{ color: 'green' }}>सूचना</span> 
              <span style={{ color: 'green', margin: '0 10px' }}>•</span> 
              <span style={{ color: 'orange' }}>समझ</span>
            </h1>
          </div>
        </div>

        {/* 3-Column Grid */}
        <div className="blog-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '60px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {blogs && blogs.length > 0 ? (
            blogs.map((blog, index) => (
              <article 
                key={blog.id || index} 
                className="insight-card"
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'  // Important: makes all cards same height
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                }}
              >
                {/* Image - Fixed height, full width */}
                <div style={{ position: 'relative', height: '260px', overflow: 'hidden' }}>
                  <Link to={`/blog/${blog.slug}`}>
                    {blog.featured_image ? (
                      <img
                        src={getImageUrl(blog.featured_image)}
                        alt={blog.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/600x260/eeeeee/999999?text=No+Image';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#aaa',
                        fontSize: '18px'
                      }}>
                        No Image Available
                      </div>
                    )}
                  </Link>
                </div>

                {/* Card Content - Takes remaining space */}
                <div style={{
                  padding: '28px 24px',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Title */}
                  <h2 style={{ margin: '0 0 14px 0', fontSize: '1.35rem', lineHeight: '1.3', fontWeight: '600' }}>
                    <Link
                      to={`/blog/${blog.slug}`}
                      style={{
                        color: '#212529',
                        textDecoration: 'none',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ff9800'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#212529'}
                    >
                      {blog.title}
                    </Link>
                  </h2>

                  {/* Date */}
                  {(blog.published_at || blog.created_at) && (
                    <div style={{
                      color: '#666',
                      fontSize: '14px',
                      marginBottom: '20px'
                    }}>
                      {new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  )}

                  {/* Read More - Pushed to bottom */}
                  <div style={{ marginTop: 'auto' }}>
                    <Link
                      to={`/blog/${blog.slug}`}
                      style={{
                        color: '#ff9800',
                        fontWeight: '600',
                        fontSize: '15px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        display: 'inline-block'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(8px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
                    >
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', fontSize: '20px', color: '#888' }}>
              No blogs available at the moment.
            </div>
          )}
        </div>

        {/* Loading indicator for more blogs */}
        {loadingMore && (
          <div className="text-center my-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading more blogs...</span>
            </div>
          </div>
        )}
        
        {/* End of results message */}
        {!hasMore && !loading && !loadingMore && blogs.length > 0 && (
          <div className="text-center my-4">
            <p className="text-muted">-</p>
          </div>
        )}

        {/* Responsive */}
        <style jsx>{`
          @media (max-width: 992px) {
            .blog-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          @media (max-width: 768px) {
            .blog-grid {
              grid-template-columns: 1fr !important;
              gap: 40px;
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default BlogListing;